import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  toastSvrNotificationWarnings,
} from "@/components/site-visits/SvrInventoryTeamFields";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Search, UserPlus, X } from "lucide-react";
import {
  buildArrivalWindowFromSlots,
  siteVisitSlotLabel,
  SITE_VISIT_SLOT_WINDOWS,
  SITE_VISIT_TOTAL_SLOTS,
  windowToSlot,
} from "@shared/siteVisitCalendarWindows";
import { parseValidArrivalWindow } from "@shared/svrSchedulingIntegrity";
import { useLocation } from "wouter";
import { navigate } from "wouter/use-browser-location";
import { logFieldCalendarUrlCrash } from "./fieldCalendarDiagnostics";
import { safeFieldCalendarNavigateHref } from "./fieldCalendarUrlState";
import { parseSingaporeDateInput, toSingaporeDateInputValue } from "@shared/timezone";

type Customer = {
  id: number;
  name: string;
  contact: string | null;
  email: string | null;
  address: string | null;
  unitNumber: string | null;
  postalCode: string | null;
};

export type CreateVisitDefaults = {
  /** Local calendar day (used for display defaults). */
  date?: Date;
  /** Pre-formatted YYYY-MM-DD in local timezone — preferred over `date`. */
  dateLocal?: string;
  startWindow?: string;
  endWindow?: string;
};

type CategoryChip = "service" | "installation" | "programming" | "others" | "on_leave";

const CATEGORY_CHIPS: { id: CategoryChip; label: string; dot: string }[] = [
  { id: "service", label: "Service", dot: "bg-orange-500" },
  { id: "installation", label: "Installation", dot: "bg-emerald-500" },
  { id: "programming", label: "Programming", dot: "bg-violet-500" },
  { id: "others", label: "Others", dot: "bg-slate-500" },
  { id: "on_leave", label: "On Leave", dot: "bg-slate-400" },
];

const WORK_TYPES = [
  "Service Visit",
  "Troubleshooting",
  "Installation Support",
  "Programming",
  "Follow-up",
  "Inspection",
];

const TIME_OPTIONS = Array.from({ length: SITE_VISIT_TOTAL_SLOTS }, (_, i) => ({
  slot: i,
  label: siteVisitSlotLabel(i),
  window: SITE_VISIT_SLOT_WINDOWS[i]!,
}));

function categoryToAssigneeRole(cat: CategoryChip): "technician" | "programmer" | "installer" {
  if (cat === "programming") return "programmer";
  if (cat === "installation") return "installer";
  return "technician";
}

function portalRoleToCategory(role: string | undefined): CategoryChip {
  if (role === "programming") return "programming";
  if (role === "installer") return "installation";
  return "service";
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaults?: CreateVisitDefaults;
  /** Full calendar path+query for SVR back navigation (e.g. `/calendar/field?date=…`). */
  returnHref?: string;
  onCreated?: () => void;
};

export function CreateServiceVisitSheet({
  open,
  onOpenChange,
  defaults,
  returnHref,
  onCreated,
}: Props) {
  const { user } = useAuth();
  const [pathname] = useLocation();
  const utils = trpc.useUtils();

  const [customerQuery, setCustomerQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [serviceType, setServiceType] = useState<"planned" | "urgent">("planned");
  const [priority, setPriority] = useState<"normal" | "high">("normal");
  const [visitDate, setVisitDate] = useState("");
  const [startWindow, setStartWindow] = useState("");
  const [endWindow, setEndWindow] = useState("");
  const startWindowRef = useRef("");
  const endWindowRef = useRef("");
  const [category, setCategory] = useState<CategoryChip>("service");
  const [assigneeId, setAssigneeId] = useState<number | "">("");
  const [workType, setWorkType] = useState("Service Visit");
  const [notes, setNotes] = useState("");
  const [followUp, setFollowUp] = useState(false);

  const isFieldStaff =
    user?.role === "service" || user?.role === "programming" || user?.role === "installer";
  const isAdmin = user?.role === "admin";

  const { data: searchResults } = trpc.customers.searchForServiceVisit.useQuery(
    { query: customerQuery },
    { enabled: customerQuery.length >= 2 && !selectedCustomer && category !== "on_leave" },
  );
  const { data: staff = [] } = trpc.siteVisits.listStaff.useQuery(undefined, { enabled: open });

  const scheduleStaff = useMemo(
    () =>
      staff
        .filter((u) => ["service", "installer", "programming", "admin"].includes(u.role))
        .sort((a, b) => String(a.name ?? "").localeCompare(String(b.name ?? ""))),
    [staff],
  );

  const resetForm = useCallback(() => {
    setCustomerQuery("");
    setSelectedCustomer(null);
    setServiceType("planned");
    setPriority("normal");
    setVisitDate("");
    setStartWindow("");
    setEndWindow("");
    startWindowRef.current = "";
    endWindowRef.current = "";
    setCategory(portalRoleToCategory(user?.role));
    setAssigneeId("");
    setWorkType("Service Visit");
    setNotes("");
    setFollowUp(false);
  }, [user?.role]);

  useEffect(() => {
    if (!open) return;
    const d = defaults?.date ?? new Date();
    setVisitDate(defaults?.dateLocal ?? toSingaporeDateInputValue(d));
    const nextStart = defaults?.startWindow ?? SITE_VISIT_SLOT_WINDOWS[4] ?? "";
    const nextEnd = defaults?.endWindow ?? SITE_VISIT_SLOT_WINDOWS[6] ?? "";
    setStartWindow(nextStart);
    setEndWindow(nextEnd);
    startWindowRef.current = nextStart;
    endWindowRef.current = nextEnd;
    setCategory(portalRoleToCategory(user?.role));
    if (user && isFieldStaff) setAssigneeId(user.id);
    else setAssigneeId("");
  }, [open, defaults, user, isFieldStaff]);

  useEffect(() => {
    if (!open) resetForm();
  }, [open, resetForm]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (priority === "high") setServiceType("urgent");
    else if (serviceType === "urgent" && priority === "normal") setServiceType("planned");
  }, [priority, serviceType]);

  const selectedStaff = scheduleStaff.find((s) => s.id === assigneeId);
  const assigneeRole = categoryToAssigneeRole(category);

  const createVisit = trpc.siteVisits.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Visit ${data.visitNumber} created`);
      toastSvrNotificationWarnings(toast, data.notificationWarnings);
      void utils.siteVisits.teamCalendarVisits.invalidate();
      onOpenChange(false);
      onCreated?.();
      const ret = safeFieldCalendarNavigateHref(returnHref ?? pathname);
      try {
        const returnTo = encodeURIComponent(ret);
        navigate(`/site-visits/id/${data.id}?returnTo=${returnTo}`, { state: { from: ret } });
      } catch (error) {
        logFieldCalendarUrlCrash("create.navigate", error, { returnHref: ret });
        navigate(`/site-visits/id/${data.id}`);
      }
    },
    onError: (e) => toast.error(e.message || "Failed to create visit"),
  });

  const createLeave = trpc.siteVisits.createTechnicianLeaveBlock.useMutation({
    onSuccess: () => {
      toast.success("Leave block added");
      void utils.siteVisits.teamCalendarLeaveBlocks.invalidate();
      onOpenChange(false);
      onCreated?.();
    },
    onError: (e) => toast.error(e.message || "Failed to add leave"),
  });

  const handleSubmit = () => {
    if (category === "on_leave") {
      const uid = typeof assigneeId === "number" ? assigneeId : user?.id;
      if (!uid || !visitDate) {
        toast.error("Select date and assignee for leave");
        return;
      }
      createLeave.mutate({
        userId: uid,
        leaveDate: parseSingaporeDateInput(visitDate),
        period: "full_day",
        notes: notes.trim() || undefined,
      });
      return;
    }

    if (!selectedCustomer) {
      toast.error("Select a customer / site");
      return;
    }
    if (!visitDate) {
      toast.error("Select a date");
      return;
    }
    const startFrom = startWindowRef.current || startWindow;
    const endFrom = endWindowRef.current || endWindow;
    if (!startFrom) {
      toast.error("Select a start time");
      return;
    }
    if (!endFrom) {
      toast.error("Select an end time");
      return;
    }
    const startSlot = windowToSlot(startFrom);
    const endSlot = windowToSlot(endFrom);
    if (startSlot < 0 || endSlot < 0) {
      toast.error("Invalid time range");
      return;
    }
    if (endSlot <= startSlot) {
      toast.error("End time must be after start time");
      return;
    }
    const scope = [workType, notes.trim()].filter(Boolean).join(" — ");
    if (!scope.trim()) {
      toast.error("Add a description or work type");
      return;
    }

    const arrivalWindow = parseValidArrivalWindow(buildArrivalWindowFromSlots(startSlot, endSlot));
    if (serviceType === "planned" && !arrivalWindow) {
      toast.error("A scheduled visit requires a valid start and end time.");
      return;
    }

    const assignees =
      selectedStaff && typeof assigneeId === "number"
        ? [
            {
              userId: assigneeId,
              userName: selectedStaff.name ?? `User ${assigneeId}`,
              role: assigneeRole,
            },
          ]
        : undefined;

    createVisit.mutate({
      customerId: selectedCustomer.id,
      visitType: serviceType,
      visitDate: parseSingaporeDateInput(visitDate),
      arrivalWindow: arrivalWindow as string | undefined,
      complaintDescription: followUp ? `[Follow-up] ${scope}` : scope,
      assignees,
      inventoryTeamRequested: false,
      inventoryTeamMessage: null,
    });
  };

  const pending = createVisit.isPending || createLeave.isPending;

  const startSlotIdx = startWindow ? windowToSlot(startWindow) : -1;
  const endSlotIdx = endWindow ? windowToSlot(endWindow) : -1;
  const durationLabel = useMemo(() => {
    if (startSlotIdx < 0 || endSlotIdx <= startSlotIdx) return null;
    const mins = (endSlotIdx - startSlotIdx) * 30;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} hr`;
    return `${h} hr ${m} min`;
  }, [startSlotIdx, endSlotIdx]);

  const endTimeOptions = TIME_OPTIONS.filter((t) =>
    startSlotIdx < 0 ? true : t.slot > startSlotIdx,
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[92vh] overflow-y-auto rounded-t-2xl px-4 pb-8 sm:max-w-lg sm:mx-auto"
      >
        <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-slate-200" />
        <SheetHeader className="text-left pb-2">
          <SheetTitle className="text-lg">
            {category === "on_leave" ? "Block leave" : "Create Service Visit"}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5 pb-4">
          {category !== "on_leave" && (
            <section className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-slate-500">Basic</Label>
              <div ref={dropdownRef} className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Customer / site"
                    value={selectedCustomer ? selectedCustomer.name : customerQuery}
                    onChange={(e) => {
                      setSelectedCustomer(null);
                      setCustomerQuery(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    className="pl-9"
                  />
                  {selectedCustomer && (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                      onClick={() => {
                        setSelectedCustomer(null);
                        setCustomerQuery("");
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {showDropdown && searchResults && searchResults.length > 0 && !selectedCustomer && (
                  <ul className="absolute z-50 mt-1 max-h-40 w-full overflow-auto rounded-lg border bg-white shadow-lg">
                    {searchResults.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                          onClick={() => {
                            setSelectedCustomer(c as Customer);
                            setCustomerQuery(c.name);
                            setShowDropdown(false);
                          }}
                        >
                          <span className="font-medium">{c.name}</span>
                          {c.address && (
                            <span className="block truncate text-xs text-slate-500">{c.address}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                className="flex items-center gap-1 text-xs font-medium text-blue-600"
                onClick={() => toast.info("Use Customers page to add new sites")}
              >
                <UserPlus className="h-3.5 w-3.5" />
                Add new customer / site
              </button>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Service type</Label>
                  <select
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value as "planned" | "urgent")}
                  >
                    <option value="planned">Service</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Priority</Label>
                  <select
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as "normal" | "high")}
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Date</Label>
                <Input
                  type="date"
                  className="mt-1"
                  value={visitDate}
                  onChange={(e) => setVisitDate(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Start time</Label>
                  <select
                    className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2.5 text-sm"
                    value={startWindow}
                    onChange={(e) => {
                      setStartWindow(e.target.value);
                      startWindowRef.current = e.target.value;
                      const s = windowToSlot(e.target.value);
                      const eIdx = windowToSlot(endWindowRef.current || endWindow);
                      if (s >= 0 && eIdx >= 0 && eIdx <= s) {
                        const next = TIME_OPTIONS.find((t) => t.slot > s);
                        if (next) {
                          setEndWindow(next.window);
                          endWindowRef.current = next.window;
                        }
                      }
                    }}
                  >
                    <option value="">Select</option>
                    {TIME_OPTIONS.map((t) => (
                      <option key={t.slot} value={t.window}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">End time</Label>
                  <select
                    className="mt-1 w-full rounded-md border border-input bg-background px-2 py-2.5 text-sm"
                    value={endWindow}
                    onChange={(e) => {
                      setEndWindow(e.target.value);
                      endWindowRef.current = e.target.value;
                    }}
                  >
                    <option value="">Select</option>
                    {endTimeOptions.map((t) => (
                      <option key={t.slot} value={t.window}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {durationLabel && (
                <p className="text-xs font-semibold text-slate-600">
                  Duration: {durationLabel}
                </p>
              )}
            </section>
          )}

          <section className="space-y-2">
            <Label className="text-xs font-semibold uppercase text-slate-500">Assignment</Label>
            <div>
              <Label className="text-xs text-slate-500">Assign to</Label>
              <select
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={assigneeId}
                disabled={isFieldStaff && !isAdmin}
                onChange={(e) => setAssigneeId(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">Select</option>
                {scheduleStaff.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.id === user?.id ? " (You)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORY_CHIPS.map((chip) => (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => setCategory(chip.id)}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                    category === chip.id
                      ? "border-blue-600 bg-blue-50 text-blue-800"
                      : "border-slate-200 bg-white text-slate-600",
                  )}
                >
                  <span className={cn("h-1.5 w-1.5 rounded-full", chip.dot)} />
                  {chip.label}
                </button>
              ))}
            </div>
          </section>

          {category !== "on_leave" && (
            <section className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-slate-500">Details</Label>
              <div>
                <Label className="text-xs text-slate-500">Work type</Label>
                <select
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={workType}
                  onChange={(e) => setWorkType(e.target.value)}
                >
                  {WORK_TYPES.map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Description / notes</Label>
                <Textarea
                  className="mt-1 min-h-[72px] resize-none"
                  maxLength={300}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What needs to be done on site?"
                />
                <p className="mt-0.5 text-right text-[10px] text-slate-400">{notes.length} / 300</p>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox checked={followUp} onCheckedChange={(c) => setFollowUp(c === true)} />
                Create follow-up task
              </label>
            </section>
          )}

          {category === "on_leave" && (
            <section className="space-y-2">
              <Label className="text-xs text-slate-500">Leave date</Label>
              <Input type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} />
              <Textarea
                className="min-h-[60px]"
                placeholder="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </section>
          )}
        </div>

        <div className="sticky bottom-0 space-y-2 border-t border-slate-100 bg-white pt-3">
          <Button
            className="w-full"
            size="lg"
            disabled={pending}
            onClick={handleSubmit}
          >
            {category === "on_leave" ? "Add leave block" : "Create Service Visit"}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
